/**
 * TimeSelect — a Select of time-of-day options whose labels render in the
 * caller's time format ('12h' → "9:00 AM", '24h' → "09:00").
 *
 * The VALUE contract is format-independent: option ids and the value/onChange
 * strings are always canonical 24-hour 'HH:mm'. Only the labels change with
 * `format`, so storage and API payloads never depend on the display
 * preference.
 *
 * Callers thread the resolved preference in (typically
 * `useOrgTimeFormat().timeFormat`) — per lib/base rules this component never
 * reads org/user context itself.
 */

import { useCallback, useMemo } from 'react';

import type {
  SelectItemType,
  SelectSize,
} from '@/lib/base/forms/select/select';
import { Select } from '@/lib/base/forms/select/select';
import { formatTime, type TimeFormat } from '@/lib/time/timeFormat';

// Re-exported so consumers (and stories) can type the format prop without
// importing lib/time directly.
export type { TimeFormat };

/** Canonical 'HH:mm' (00:00–23:59). Local to lib/base — the crm variant of
 * this regex (lib/crm/utils/workingHours.isValidTimeString) cannot be imported
 * from here without breaking the base/feature boundary. */
const TIME_VALUE_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Render the display label for a canonical 'HH:mm' time in the given format. */
// eslint-disable-next-line react-refresh/only-export-components -- pure label helper co-located with the component (same pattern as select.tsx)
export function timeOptionLabel(time: string, format: TimeFormat): string {
  const [hh = 0, mm = 0] = time.split(':').map(Number);
  // Synthetic local date — only the wall-clock time matters for the label.
  return formatTime(new Date(1970, 0, 1, hh, mm), format);
}

/**
 * Build the option list: one item per `stepMinutes` across the day, ids in
 * canonical 'HH:mm', labels in the requested format.
 */
// eslint-disable-next-line react-refresh/only-export-components -- pure item-list builder co-located with the component (same pattern as select.tsx)
export function generateTimeSelectItems(
  format: TimeFormat,
  stepMinutes: number = 30
): SelectItemType[] {
  // A non-positive step would make the loop below never advance, hanging the
  // render. Clamp to a positive whole-minute step so bad input degrades to the
  // default cadence rather than freezing the UI.
  const step =
    Number.isFinite(stepMinutes) && stepMinutes >= 1
      ? Math.floor(stepMinutes)
      : 30;
  const items: SelectItemType[] = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += step) {
    const hh = String(Math.floor(minutes / 60)).padStart(2, '0');
    const mm = String(minutes % 60).padStart(2, '0');
    const id = `${hh}:${mm}`;
    items.push({ id, label: timeOptionLabel(id, format) });
  }
  return items;
}

// Hoisted render prop — stable identity, no per-render closure.
const renderItem = (item: SelectItemType) => (
  <Select.Item id={item.id}>{item.label}</Select.Item>
);

export interface TimeSelectProps {
  /** Display format for option labels. Thread `useOrgTimeFormat().timeFormat` in. */
  format: TimeFormat;
  /** Canonical 24-hour 'HH:mm' value (e.g. '09:30'), or null when unset. */
  value: string | null;
  /** Receives the canonical 'HH:mm' id of the chosen option. */
  onChange: (time: string) => void;
  /** Minutes between consecutive options. Default 30. */
  stepMinutes?: number;
  size?: SelectSize;
  isDisabled?: boolean;
  'aria-label'?: string;
  placeholder?: string;
  className?: string;
}

export const TimeSelect = ({
  format,
  value,
  onChange,
  stepMinutes = 30,
  size = 'sm',
  isDisabled = false,
  'aria-label': ariaLabel,
  placeholder,
  className,
}: TimeSelectProps) => {
  const items = useMemo(() => {
    const generated = generateTimeSelectItems(format, stepMinutes);
    // A value off the step grid (external data, a different prior step) must
    // still display — insert it in chronological position rather than
    // rendering an empty trigger.
    if (
      value !== null &&
      TIME_VALUE_PATTERN.test(value) &&
      !generated.some(item => item.id === value)
    ) {
      const insertAt = generated.findIndex(item => String(item.id) > value);
      const extra = { id: value, label: timeOptionLabel(value, format) };
      if (insertAt === -1) {
        generated.push(extra);
      } else {
        generated.splice(insertAt, 0, extra);
      }
    }
    return generated;
  }, [format, stepMinutes, value]);

  const handleSelectionChange = useCallback(
    (key: string | number | null) => {
      if (typeof key === 'string') {
        onChange(key);
      }
    },
    [onChange]
  );

  return (
    <Select
      size={size}
      items={items}
      value={value}
      isDisabled={isDisabled}
      onChange={handleSelectionChange}
      aria-label={ariaLabel}
      placeholder={placeholder}
      className={className}
    >
      {renderItem}
    </Select>
  );
};
